/**
 * Krok PROP1 — JEDNA MAPA UDAJU NEMOVITOSTI.
 *
 * Do PROP1 sbiral REOS tyz udaj na TREH mistech, kazde s vlastnim mapovanim:
 *  1. nabor na miste zapisoval FAKTA zakazky (`setFact`) — cetl je koncept
 *     inzeratu, smlouva ne;
 *  2. klientsky dotaznik zapisoval jen POLE SMLOUVY
 *     (`client-intake/property-propagate.ts`) — fakta ani inzerat o nich
 *     nevedely;
 *  3. smlouva cetla z faktu jen katastralni udaje (`cadastre-prefill.ts`).
 *
 * Makler a majitel proto zadavali dispozici, plochu, patro nebo vytapeni
 * dvakrat az trikrat. Tahle mapa je JEDINE misto, kde se rika, ze „uzitna
 * plocha" ve faktech, v poli smlouvy a v konceptu inzeratu je TATAZ VEC —
 * vcetne prevodu hodnoty mezi nimi (fakt -> smlouva, dotaznik -> fakt).
 *
 * PROC TADY (v `@reos/schemas`, ne v kontextu): klice faktu zijí v registru
 * `FACT_KEYS` tady, pole smlouvy v `@reos/documents` a pole inzeratu
 * v `@reos/listing`. Kontexty se navzajem importovat nesmi (CLAUDE.md
 * pravidlo 4), takze spolecny slovnik musi byt na platforme. Ze kazdy radek
 * sedi na skutecna pole, hlida test v KAZDEM z tech kontextu — mapa sama
 * nazvy poli nevalidovat nemuze.
 *
 * NOVE KLICE SE JMENUJI PODLE KATALOGU naboru bytu (`catalogId` nese odlisnost
 * u starsich klicu, ktere se neprejmenovavaji).
 */
import { requireFactKey } from './fact-keys.js';
/**
 * Jak se hodnota prevadi mezi faktem a polem smlouvy.
 *
 * `boolean_yes_no`: fakt je `true`/`false`, smlouva ma typ `volba` a cesky
 * text „ano"/„ne" — presne jak ho dosud psal dotaznik (`yesNo`), aby se
 * sazba smlouvy nezmenila.
 */
export const PROPERTY_FIELD_KINDS = [
    'text',
    'number',
    'integer',
    'boolean_yes_no',
    'date',
    'list',
    /*
     * Krok NAB3: polozky s poli (mistnosti). Do smlouvy jde z kazde polozky
     * SOUHRNNA VETA (`summary`) — smlouva ma typ `seznam`, ne tabulku.
     */
    'object_list',
    /*
     * Krok PROP5: penezni fakt (`Money` = numeric string + ISO 4217) v poli
     * smlouvy typu `castka-kc`, ktere je CISLO v korunach. Prevod je tady,
     * aby ho kazda cesta (dotaznik, nabor, doplneni smlouvy) delala stejne;
     * jina mena nez CZK se do pole `castka-kc` nedosadi (pravidlo L0: lepsi
     * prazdno nez castka ve spatne mene).
     */
    'money_czk',
];
function row(factKey, contractKey, kind, options = {}) {
    return {
        factKey,
        contractKey,
        kind,
        draftField: options.draftField ?? null,
        catalogId: options.catalogId ?? null,
        alsoContractKeys: options.alsoContractKeys ?? [],
    };
}
/** Jediny seznam „tenhle fakt = tohle pole smlouvy = tohle pole inzeratu". */
export const PROPERTY_FIELD_MAP = [
    row('disposition', 'nemovitost.dispozice', 'text', {
        draftField: 'disposition',
        catalogId: 'property_layout',
    }),
    row('usable_area_m2', 'nemovitost.uzitna-plocha', 'number', { draftField: 'usable_area_m2' }),
    row('floor_number', 'nemovitost.patro', 'integer', {
        draftField: 'floor_number',
        catalogId: 'unit_floor',
        alsoContractKeys: ['nemovitost.podlazi'],
    }),
    /*
     * Krok P8F4 bod 1 — CISLO JEDNOTKY. Fakt `unit_number` uz nabor i provereni
     * zapisovaly, mapa ho ale nenesla, takze pole `nemovitost.jednotka-cislo`
     * umel doplnit JEN katastr — a najemni smlouva po naboru hlasila
     * „Jednotka — číslo Chybí" (nalez P8-N5). Katastr dal vyhrava: ten se
     * v `cadastre-prefill.ts` dosazuje PRVNI a fakt uz obsazene pole neprepise.
     */
    row('unit_number', 'nemovitost.jednotka-cislo', 'text'),
    row('has_elevator', 'nemovitost.vytah', 'boolean_yes_no', {
        draftField: 'has_elevator',
        catalogId: 'building_elevator',
    }),
    row('has_cellar', 'nemovitost.sklep', 'boolean_yes_no', {
        draftField: 'has_cellar',
        catalogId: 'cellar',
    }),
    row('has_balcony', 'nemovitost.balkon', 'boolean_yes_no', {
        draftField: 'has_balcony',
        catalogId: 'balcony',
    }),
    row('has_loggia', 'nemovitost.lodzie', 'boolean_yes_no', {
        draftField: 'has_loggia',
        catalogId: 'loggia',
    }),
    row('has_terrace', 'nemovitost.terasa', 'boolean_yes_no', {
        draftField: 'has_terrace',
        catalogId: 'terrace',
    }),
    row('parking_type', 'nemovitost.parkovani', 'text'),
    row('heating_type', 'nemovitost.vytapeni', 'text', {
        draftField: 'heating_type',
        catalogId: 'heating_system',
    }),
    row('build_year', 'nemovitost.rok-vystavby', 'integer', {
        draftField: 'build_year',
        catalogId: 'building_year',
    }),
    row('condition', 'nemovitost.stav', 'text', {
        draftField: 'building_condition',
        catalogId: 'building_condition',
    }),
    row('ownership_type', 'nemovitost.vlastnictvi', 'text', { draftField: 'ownership_type' }),
    row('building_roof_renovation_year', 'nemovitost.rekonstrukce-strecha', 'integer'),
    row('room_window_replacement_year', 'nemovitost.rekonstrukce-okna', 'integer'),
    row('wiring_reconstruction_year', 'nemovitost.rekonstrukce-rozvody', 'integer'),
    row('heating_boiler_year', 'nemovitost.rekonstrukce-zdroj-tepla', 'integer'),
    row('energy_class', 'nemovitost.penb-trida', 'text', { draftField: 'energy_class' }),
    row('audit_penb_valid_until', 'nemovitost.penb-platnost', 'date'),
    row('equipment_included', 'nemovitost.vybaveni[]', 'list', {
        catalogId: 'rental_furnished_inventory',
    }),
    row('flat_defect_list', 'nemovitost.vady[]', 'list'),
    /*
     * Krok NAB3 — MISTNOSTI. Fakt je seznam objektu (jedna mistnost = jedna
     * polozka), smlouva je nese jako `seznam` vet („Pokoj 14 m², plovoucí
     * podlaha…"). Vetu sklada ten, kdo mistnost zapisuje — ma k ni sablonu
     * s popisky (`field-intake`); mapa ji jen vybere, aby popisky polí
     * nevznikly podruhe tady.
     */
    row('room_list', 'nemovitost.mistnosti[]', 'object_list', { catalogId: 'rooms' }),
    /*
     * Krok PROP7 bod 1 — TERMIN PREDANI JEDNOU PRO VSECHNY TRI CESTY.
     *
     * PROP5 uz sjednotil KLIC (`handover_from` -> `sale.available_from`,
     * `FACT_KEY_ALIASES`), takze nabor, inzerat i verejna stranka ctou tentyz
     * fakt. Smlouva ale v mape CHYBELA: pole `termin.predani` ma zdroj
     * „zakazka", a presto ho „Doplnit ze zakázky" nemelo odkud vzit — makler
     * datum, ktere na prohlidce zapsal, psal do smlouvy znovu rucne.
     *
     * `draftField` je `available_from`, tedy tyz udaj, ktery uz cte koncept
     * inzeratu (`DRAFT_FIELDS` v `@reos/listing`) — jeden radek drzi vsechny
     * tri cile, aby se nerozesly.
     */
    row('sale.available_from', 'termin.predani', 'date', { draftField: 'available_from' }),
    row('handover_electricity_meter', 'meridla.elektrina', 'text'),
    row('handover_gas_meter', 'meridla.plyn', 'text'),
    row('handover_hot_water_meter', 'meridla.voda-tepla', 'text', { catalogId: 'handover_water_meters' }),
    row('handover_cold_water_meter', 'meridla.voda-studena', 'text', {
        catalogId: 'handover_water_meters',
    }),
    /*
     * Krok NAB5 bod 4 — CISLO MERIDLA. Nabor ho vidi na meridle uz na
     * prohlidce; stav (odecet) vznika az pri predani a ma radky vyse. Bez
     * tehchto radku melo pole protokolu zdroj „zakazka", ale „Doplnit ze
     * zakázky" pro nej nemelo odkud brat — makler cislo psal podruhe rucne.
     */
    row('handover_electricity_meter_number', 'meridla.elektrina-cislo', 'text'),
    row('handover_gas_meter_number', 'meridla.plyn-cislo', 'text'),
    row('handover_hot_water_meter_number', 'meridla.voda-tepla-cislo', 'text'),
    row('handover_cold_water_meter_number', 'meridla.voda-studena-cislo', 'text'),
    /*
     * Krok PROP5 — SVJ JEDNOU. Radky drzi i rozdil, ktery se pred PROP5
     * ztracel: `svj.zalohy-mesicne` je cela mesicni platba, `fond-oprav-mesicne`
     * jeji cast na spravu domu a `fond-oprav-stav` JEDNORAZOVY zustatek fondu.
     * Nazev a ICO maji pole smlouvy pod `nemovitost.*` uz od slovniku T1.
     */
    row('svj_name', 'nemovitost.svj-nazev', 'text'),
    row('svj_entity_id', 'nemovitost.svj-ico', 'text'),
    /*
     * Krok ZAK1 bod 3 — KATASTRALNI UZEMI DO SMLOUVY. Nazev k. u. umi od ZAK1
     * dodat RUIAN (vrstva 7, parcela pod stavbou), takze uz nemusi cekat na
     * list vlastnictvi. Bez tohoto radku by hodnota dosla do karty nemovitosti
     * i do naboru, ale „Doplnit ze zakázky" by u `nemovitost.ku-nazev` dal
     * mlcelo (`propertyFieldCanPrefill` = false) a makler by ji psal podruhe.
     * KOD k. u. (`nemovitost.ku-kod`) dostal radek az v ZAK1b, kdy pro nej
     * vznikl klic registru (`cadastral_territory_code`, vrstva 7 RUIAN) —
     * do ZAK1b se cislo nehadalo z nazvu a pole smlouvy zustavalo prazdne.
     */
    row('cadastral_territory', 'nemovitost.ku-nazev', 'text'),
    row('cadastral_territory_code', 'nemovitost.ku-kod', 'integer'),
    /*
     * Krok ZAK1 bod 4 — CISLO LV a PODIL NA JEDNOTCE. Oba udaje umi dodat jen
     * list vlastnictvi (`wsdp/lv-prefill.ts`), takze se do smluv dostanou az
     * s povolenym nactenim LV; radek mapy ale musi existovat uz ted, jinak by
     * „Doplnit ze zakázky" u tech poli mlcelo i po zapnuti.
     */
    row('land_registry_sheet', 'nemovitost.lv-cislo', 'text'),
    row('unit_share', 'nemovitost.jednotka-podil', 'text'),
    /*
     * Krok LV1fix2 bod 5 — DATUM VYPISU DO SMLOUVY. Zivy test nasel, ze
     * `nemovitost.lv-datum` zustalo „bez-faktu", i kdyz zaplaceny vypis datum
     * vyhotoveni nese. Fakt karty vznikl touz opravou
     * (`land_registry_issued_on`, projekce `ownership.lv`); bez radku tady by
     * „Doplnit z katastru" u toho pole mlcelo dal.
     */
    row('land_registry_issued_on', 'nemovitost.lv-datum', 'date'),
    row('svj_advances_monthly', 'svj.zalohy-mesicne', 'money_czk'),
    row('svj_contribution_monthly', 'svj.fond-oprav-mesicne', 'money_czk'),
    row('svj_repair_fund_balance', 'svj.fond-oprav-stav', 'money_czk'),
    row('svj_finance_debt', 'svj.uver', 'boolean_yes_no'),
    row('svj_advances_for_people', 'svj.zalohy-osoby', 'integer'),
    row('sale.has_lien', 'zavazky.hypoteka', 'boolean_yes_no', {
        draftField: 'has_lien',
        catalogId: 'mortgage_or_lien',
    }),
    row('execution_or_insolvency', 'zavazky.exekuce', 'boolean_yes_no'),
    row('unit_tenanted', 'zavazky.najemnici', 'boolean_yes_no'),
    /*
     * Krok P8F4 bod 5 (rozhodnuti R.B. 3) — NABIDKOVA CENA DO ZPROSTREDKOVATELSKE.
     *
     * `asking_price` je JEDINA aktivni nabidkova cena karty (viz `fact-keys.ts`:
     * inzerat ani verejna stranka jinou necte a na zakazku se cena v pilotu
     * neprenasi). Vyhradni zprostredkovatelska smlouva ji dosud nemela vubec,
     * takze ji makler psal rucne nebo neuvedl (nalez P8-N5).
     */
    row('asking_price', 'obchod.nabidkova-cena', 'money_czk'),
];
/** Fakta „ano/ne" slozena do vety — popisek do vety pise tenhle seznam. */
const ACCESSORY_LABELS = [
    ['has_cellar', 'sklep'],
    ['has_balcony', 'balkon'],
    ['has_loggia', 'lodžie'],
];
/**
 * NEZNAME NENI „NENI": fakt, ktery karta nema, se do vety nedostane ani jako
 * popreni. Veta vznika jen z faktu, ktere jsou PROKAZATELNE `true`; kdyz
 * takovy neni, pole zustane prazdne a makler ho vyplni sam (pravidlo L0 —
 * lepsi prazdno nez tvrzeni „bez příslušenství", ktere nikdo nerekl).
 */
function composeAccessories(values) {
    const parts = ACCESSORY_LABELS.filter(([factKey]) => values.get(factKey) === true).map(([, label]) => label);
    return parts.length === 0 ? null : parts.join(', ');
}
export const PROPERTY_COMPOSED_FIELDS = [
    {
        contractKey: 'nemovitost.prislusenstvi',
        factKeys: ACCESSORY_LABELS.map(([factKey]) => factKey),
        compose: composeAccessories,
    },
];
const COMPOSED_BY_CONTRACT_KEY = new Map(PROPERTY_COMPOSED_FIELDS.map((entry) => [entry.contractKey, entry]));
export function propertyComposedFieldByContractKey(contractKey) {
    return COMPOSED_BY_CONTRACT_KEY.get(contractKey);
}
const BY_FACT_KEY = new Map(PROPERTY_FIELD_MAP.map((entry) => [entry.factKey, entry]));
/**
 * Krok P8F4 bod 1: vedle hlavniho klice jsou v mape i DALSI klice tehoz pojmu
 * (`alsoContractKeys`) — „Doplnit ze zakázky" tak plni `nemovitost.podlazi`
 * ze stejneho faktu jako `nemovitost.patro`.
 */
const BY_CONTRACT_KEY = new Map(PROPERTY_FIELD_MAP.flatMap((entry) => [entry.contractKey, ...entry.alsoContractKeys].map((key) => [key, entry])));
export function propertyFieldByFactKey(factKey) {
    return BY_FACT_KEY.get(factKey);
}
export function propertyFieldByContractKey(contractKey) {
    return BY_CONTRACT_KEY.get(contractKey);
}
/** Klice poli smlouvy, ktera umi doplnit fakta zakazky („Doplnit ze zakázky"). */
export function mappedContractKeys() {
    return [
        ...PROPERTY_FIELD_MAP.flatMap((entry) => [entry.contractKey, ...entry.alsoContractKeys]),
        ...PROPERTY_COMPOSED_FIELDS.map((entry) => entry.contractKey),
    ];
}
/**
 * Umi „Doplnit ze zakázky" tohle pole smlouvy? Jedno misto pro osnovu
 * (`fill-plan.ts`) i pro samo doplneni (`cadastre-prefill.ts`) — kdyz se ty
 * dve odpovedi rozejdou, osnova pole nenabidne a doplneni se k nemu nedostane.
 */
export function propertyFieldCanPrefill(contractKey) {
    return (BY_CONTRACT_KEY.has(contractKey) || COMPOSED_BY_CONTRACT_KEY.has(contractKey));
}
function cleanList(value) {
    if (!Array.isArray(value))
        return null;
    const list = value
        .filter((item) => typeof item === 'string')
        .map((item) => item.trim().slice(0, 200))
        .filter((item) => item !== '')
        .slice(0, 50);
    return list.length === 0 ? null : list;
}
/**
 * Krok NAB3 — SOUHRNNE VETY polozek (mistnosti) pro pole smlouvy typu
 * `seznam`. Vetu nese polozka v poli `summary`: slozil ji zapis z sablony
 * mistnosti, ktera jediná zna popisky poli. Polozka bez vety se vynecha
 * (pravidlo L0: lepsi prazdno nez vymyslena hodnota).
 */
function cleanSummaries(value) {
    if (!Array.isArray(value))
        return null;
    return cleanList(value.map((item) => typeof item === 'object' && item !== null ? item['summary'] : null));
}
function finiteNumber(value) {
    const parsed = typeof value === 'string' ? Number(value.replace(',', '.')) : value;
    return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
}
/**
 * Krok PROP5 — `Money` faktu na cislo v korunach. Castky z DB chodi jako
 * `numeric(18,4)`, tedy „3500.0000" (poučeni UXP5 bodu 2 u ceny): bez tohohle
 * prevodu by pole smlouvy dostalo retezec a sazba by ho vytiskla s nulami.
 */
function moneyCzkToNumber(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const money = value;
    if (money['currency'] !== 'CZK')
        return null;
    const amount = finiteNumber(money['amount']);
    return amount === null ? null : Math.round(amount * 100) / 100;
}
/**
 * Hodnota faktu -> hodnota pole smlouvy. `null` = fakt se do pole dosadit
 * neda (prazdna hodnota, spatny typ) — pole zustane prazdne a doplneni rekne
 * proc (pravidlo L0: lepsi prazdno nez vymyslena hodnota).
 */
export function factValueToContractValue(row, value) {
    if (value === null || value === undefined)
        return null;
    switch (row.kind) {
        case 'boolean_yes_no':
            return typeof value === 'boolean' ? (value ? 'ano' : 'ne') : null;
        case 'number': {
            const parsed = finiteNumber(value);
            return parsed;
        }
        case 'integer': {
            const parsed = finiteNumber(value);
            return parsed === null || !Number.isInteger(parsed) ? null : parsed;
        }
        case 'list':
            return cleanList(value);
        case 'object_list':
            return cleanSummaries(value);
        case 'money_czk':
            return moneyCzkToNumber(value);
        case 'text': {
            const text = String(value).trim();
            return text === '' ? null : text;
        }
        /*
         * Krok P8F8 bod 1 — DOSTUPNOST SLOVY SE DO POLE „DATUM" NEDOSADI.
         *
         * `sale.available_from` je v registru TEXT: majitel rekne „Po dohodě"
         * stejne casto jako konkretni den. Pole smlouvy `termin.predani` je ale
         * typu `datum` (slovnik `@reos/documents`), takze „Po dohodě" by se do nej
         * dosadilo jako nesrozumitelna hodnota a datovy prvek editoru by ji
         * neukazal. Pravidlo L0: lepsi PRAZDNO (a „doplní se později" u pole) nez
         * hodnota, kterou pole neumi nest — a nez vymyslene datum. Dostupnost
         * slovy zustava na karte a na verejne strance, kde ji lidi ctou.
         */
        case 'date': {
            const text = String(value).trim();
            if (text === '')
                return null;
            return /^\d{4}-\d{2}-\d{2}/.test(text) || /^\d{1,2}\.\s*\d{1,2}\.\s*\d{4}$/.test(text) ? text : null;
        }
    }
}
/**
 * Hodnota z klientskeho dotazniku -> hodnota faktu. Prochazi TOUTEZ mapou,
 * takze se dotaznik a smlouva nemohou rozejit; vysledek jeste validuje
 * registr (`validateFactValue` v `setFact`).
 */
export function clientValueToFactValue(row, value) {
    if (value === null || value === undefined)
        return null;
    switch (row.kind) {
        case 'boolean_yes_no':
            return typeof value === 'boolean' ? value : null;
        case 'number':
            return finiteNumber(value);
        case 'integer': {
            const parsed = finiteNumber(value);
            return parsed === null || !Number.isInteger(parsed) ? null : parsed;
        }
        case 'list':
            return cleanList(value);
        case 'money_czk': {
            // Dotaznik posila koruny jako cislo; fakt je `Money` (CLAUDE.md §3).
            const amount = finiteNumber(value);
            return amount === null || amount < 0
                ? null
                : { amount: (Math.round(amount * 100) / 100).toFixed(2), currency: 'CZK' };
        }
        case 'object_list':
            /*
             * Mistnosti klientsky dotaznik nesbira — vznikaji na prohlidce z dispozice
             * (NAB3). Kdyby je zacal posilat, musi k nim dodat i sablonu, jinak by
             * fakt dostal polozky bez poli.
             */
            return null;
        case 'text':
        case 'date': {
            const text = String(value).trim();
            // Registr ma u textu strop 500 znaku; delsi odpoved dotazniku je chyba
            // vstupu, ne fakt — radeji se zkrati, nez aby `setFact` vratil 422.
            return text === '' ? null : text.slice(0, 500);
        }
    }
}
/** Fakt podle registru; drzi invariant „kazdy radek mapy je znamy fact_key". */
export function assertPropertyFieldMapFactKeys() {
    for (const entry of PROPERTY_FIELD_MAP)
        requireFactKey(entry.factKey);
}
//# sourceMappingURL=property-field-map.js.map